import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface EmployeeSessionContextType {
  sessionId: string;
}

const EmployeeSessionContext = createContext<EmployeeSessionContextType | undefined>(undefined);

const SESSION_STORAGE_KEY = 'payitsmart_employee_add_session_id';

function generateSessionId() {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

export function useEmployeeSession() {
  const context = useContext(EmployeeSessionContext);
  if (context === undefined) {
    console.warn('useEmployeeSession called outside EmployeeSessionProvider. Falling back to temporary session id.');
    return { sessionId: generateSessionId() };
  }
  return context;
}

export function EmployeeSessionProvider({ children }: { children: ReactNode }) {
  const [sessionId] = useState(() => {
    if (typeof window === 'undefined') {
      return generateSessionId();
    }
    const storedId = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (storedId) {
      return storedId;
    }
    const generated = generateSessionId();
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, generated);
    return generated;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }, [sessionId]);

  return (
    <EmployeeSessionContext.Provider value={{ sessionId }}>
      {children}
    </EmployeeSessionContext.Provider>
  );
}

export function clearEmployeeSessionId() {
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }
}
